/**
 * BookingFlowContext
 * Multi-step booking wizard state management
 * Manages schedule, package, and location details with localStorage persistence
 */

import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { v4 as uuidv4 } from 'uuid'

const BookingFlowContext = createContext({})

// Step configuration
// Actual booking flow: Schedule → Add-Ons → Account Setup → Contract → Payment
const BOOKING_STEPS = [
  { id: 'package', label: 'Choose Package', order: 0 },
  { id: 'addons', label: 'Add-Ons', order: 1 },
  { id: 'account', label: 'Account Setup', order: 2 },
  { id: 'contract', label: 'Contract & Signature', order: 3 },
  { id: 'payment', label: 'Payment', order: 4 }
]

// Generate or retrieve a persistent session ID
const getBookingSessionId = () => {
  let sessionId = sessionStorage.getItem('booking_session_id')
  if (!sessionId) {
    sessionId = `booking_${uuidv4()}`
    sessionStorage.setItem('booking_session_id', sessionId)
  }
  return sessionId
}

export const useBookingFlow = () => {
  const context = useContext(BookingFlowContext)
  if (!context) {
    throw new Error('useBookingFlow must be used within BookingFlowProvider')
  }
  return context
}

export const BookingFlowProvider = ({ children }) => {
  const [sessionId] = useState(getBookingSessionId)
  const [bookingFlow, setBookingFlow] = useState({
    photographerId: null,
    videographerId: null, // Optional videographer ID for video add-on
    secondPhotographerId: null, // Optional second photographer ID for second-shooter add-on
    engagementBookingId: null, // Database booking ID for engagement session (separate booking)
    bookingId: null, // Database booking ID once created
    currentStep: 'schedule',
    completedSteps: [],

    scheduleDetails: {
      date: null,
      startTime: null,
      endTime: null,
      selectedAt: null
    },

    packageDetails: {
      packageType: null, // 'monthly' | 'deposit'
      packagePrice: null, // Base package price
      hoursBooked: null, // Number of hours for the package
      isPhotoVideo: false, // Whether package includes video
      packageTitle: null, // Display title for the package
      selectedAt: null
    },

    addonsDetails: {
      selectedAddons: [], // Array of {id, title, price, qty}
      totalAddonsPrice: 0, // Computed total of all add-ons
      selectedAt: null
    },

    locationDetails: {
      city: null,
      state: null,
      locationTitle: null,
      address: null,
      lat: null,  // from Google Geocoding / Places
      lng: null,
      selectedAt: null
    },

    travelFeeDetails: {
      travelFee: 0,          // Travel fee in dollars
      distanceMiles: null,   // Distance to nearest major metro
      nearestMetro: null,    // e.g. "Phoenix, AZ"
      calculatedAt: null
    },

    accountDetails: {
      isAuthenticated: false,
      userId: null,
      email: null,
      fullName: null,
      phone: null,
      selectedAt: null
    },

    contractDetails: {
      contractSigned: false,
      contractSignatureId: null,
      contractVersion: null,
      signedAt: null,
      selectedAt: null
    },

    paymentDetails: {
      paymentCompleted: false,
      paymentMethod: null,
      paymentPlan: 'full', // 'full', 'deposit+3', or 'installments'
      paymentIntentId: null,
      receiptUrl: null,
      paidAt: null,
      selectedAt: null
    },

    promotionDetails: {
      promotionId: null,
      code: null,
      name: null,
      discount_type: null,
      discount_value: null,
      free_addons: [],
      appliedAt: null
    },

    validationState: {
      schedule: false,
      package: false,
      addons: false,
      account: false,
      contract: false,
      payment: false
    }
  })

  // Generate localStorage key for a specific photographer
  const getStorageKey = useCallback((photographerId) => {
    return `booking_flow_${photographerId}_${sessionId}`
  }, [sessionId])

  // Load existing booking flow from localStorage
  const loadBookingFlow = useCallback((photographerId) => {
    if (!photographerId) return null

    try {
      const stored = localStorage.getItem(getStorageKey(photographerId))
      if (stored) {
        const parsed = JSON.parse(stored)

        // Validate that the stored data matches current photographer
        if (parsed.photographerId !== photographerId) {
          console.log('🔄 Photographer ID mismatch - clearing stale booking flow')
          localStorage.removeItem(getStorageKey(photographerId))
          return null
        }

        // Check if booking flow has expired (24 hours TTL)
        if (parsed.expiresAt) {
          const expirationDate = new Date(parsed.expiresAt)
          const now = new Date()

          if (now > expirationDate) {
            console.log('⏰ Booking flow expired - clearing stale data:', {
              expiresAt: parsed.expiresAt,
              now: now.toISOString(),
              hoursExpired: Math.round((now - expirationDate) / (1000 * 60 * 60))
            })
            localStorage.removeItem(getStorageKey(photographerId))
            return null
          }
        }

        console.log('✅ Loaded valid booking flow from localStorage:', {
          photographerId,
          date: parsed.scheduleDetails?.date,
          updatedAt: parsed.updatedAt,
          expiresAt: parsed.expiresAt
        })

        return parsed
      }
    } catch (error) {
      console.error('Failed to load booking flow:', error)
    }
    return null
  }, [getStorageKey])

  // Save booking flow to localStorage
  const saveBookingFlow = useCallback((flow) => {
    if (!flow.photographerId) return

    try {
      const now = new Date()
      const expiresAt = new Date(now.getTime() + 24 * 60 * 60 * 1000) // 24 hours from now

      localStorage.setItem(getStorageKey(flow.photographerId), JSON.stringify({
        ...flow,
        updatedAt: now.toISOString(),
        expiresAt: expiresAt.toISOString()
      }))
    } catch (error) {
      console.error('Failed to save booking flow:', error)
    }
  }, [getStorageKey])

  // Auto-save whenever booking flow changes
  useEffect(() => {
    if (bookingFlow.photographerId) {
      saveBookingFlow(bookingFlow)
    }
  }, [bookingFlow, saveBookingFlow])

  // Auto-load existing booking flow from localStorage on mount
  useEffect(() => {
    // Only run once on mount
    if (bookingFlow.photographerId) return // Already initialized

    // Try to find any existing booking flow in localStorage for this session
    try {
      const allKeys = Object.keys(localStorage)
      const bookingKeys = allKeys.filter(key =>
        key.startsWith('booking_flow_') && key.endsWith(`_${sessionId}`)
      )

      if (bookingKeys.length > 0) {
        // Load the most recent booking flow (in case there are multiple)
        const mostRecentKey = bookingKeys[bookingKeys.length - 1]
        const stored = localStorage.getItem(mostRecentKey)

        if (stored) {
          const parsed = JSON.parse(stored)
          // Same 24h rule as loadBookingFlow: a stale wizard must not come back on its own (20 Sep 2026)
          if (parsed.expiresAt && new Date() > new Date(parsed.expiresAt)) {
            localStorage.removeItem(mostRecentKey)
          } else {
            console.log('🔄 Auto-loading booking flow from localStorage:', parsed.photographerId)
            setBookingFlow(parsed)
          }
        }
      }
    } catch (error) {
      console.error('Failed to auto-load booking flow:', error)
    }
  }, [sessionId]) // Only run when sessionId is available, and only once

  // Initialize booking flow for a photographer
  const initializeBookingFlow = useCallback((photographerId, initialData = {}) => {
    console.log('🔧 initializeBookingFlow called:', {
      photographerId,
      providedDate: initialData.date,
      hasPackage: !!(initialData.packageDetails?.packagePrice)
    })

    // Try to load existing flow first
    const existingFlow = loadBookingFlow(photographerId)

    if (existingFlow) {
      console.log('📋 Found existing booking flow:', {
        existingDate: existingFlow.scheduleDetails?.date,
        newDate: initialData.date,
        willUpdateDate: !!initialData.date
      })

      // If a new date is provided, check if it's different from the existing date
      // If date changed, start completely fresh to avoid reusing old booking data
      if (initialData.date) {
        const dateChanged = existingFlow.scheduleDetails?.date !== initialData.date

        // If date changed, clear ALL booking data and start fresh
        if (dateChanged) {
          console.log('🔄 Date changed - starting fresh booking:', {
            oldDate: existingFlow.scheduleDetails?.date,
            newDate: initialData.date
          })

          const freshFlow = {
            photographerId: existingFlow.photographerId,
            videographerId: null,
            secondPhotographerId: null,
            engagementBookingId: null,
            bookingId: null, // Clear old booking ID
            currentStep: 'schedule',
            completedSteps: ['schedule'], // Only schedule is complete
            scheduleDetails: {
              date: initialData.date,
              startTime: initialData.startTime || null,
              endTime: initialData.endTime || null,
              selectedAt: new Date().toISOString()
            },
            // Clear package selection - user must reselect
            packageDetails: {
              packageType: null,
              packagePrice: null,
              hoursBooked: null,
              isPhotoVideo: false,
              packageTitle: null,
              selectedAt: null
            },
            // Clear addons - user must reselect
            addonsDetails: {
              selectedAddons: [],
              totalAddonsPrice: 0,
              selectedAt: null
            },
            // Keep location details (may still be relevant)
            locationDetails: existingFlow.locationDetails,
            // Clear travel fee (will be recalculated)
            travelFeeDetails: {
              travelFee: 0,
              distanceMiles: null,
              nearestMetro: null,
              calculatedAt: null
            },
            // Keep account details (user is still logged in)
            accountDetails: existingFlow.accountDetails,
            // Clear contract - new booking needs new contract
            contractDetails: {
              contractSigned: false,
              contractSignatureId: null,
              contractVersion: null,
              signedAt: null,
              selectedAt: null
            },
            // Clear payment - new booking needs new payment
            paymentDetails: {
              paymentCompleted: false,
              paymentMethod: null,
              paymentPlan: 'full',
              paymentIntentId: null,
              receiptUrl: null,
              paidAt: null,
              selectedAt: null
            },
            validationState: {
              schedule: true,
              package: false,
              addons: false,
              account: existingFlow.validationState.account, // Keep if already validated
              contract: false,
              payment: false
            }
          }

          setBookingFlow(freshFlow)
          return freshFlow
        } else {
          // Same date - just update the flow without clearing
          const updatedFlow = {
            ...existingFlow,
            scheduleDetails: {
              date: initialData.date,
              startTime: initialData.startTime || null,
              endTime: initialData.endTime || null,
              selectedAt: new Date().toISOString()
            },
            completedSteps: [...new Set([...existingFlow.completedSteps, 'schedule'])],
            validationState: {
              ...existingFlow.validationState,
              schedule: true
            }
          }

          console.log('✅ Updated existing flow (same date):', {
            date: initialData.date
          })

          setBookingFlow(updatedFlow)
          return updatedFlow
        }
      }

      // No new date provided - use existing flow as-is
      setBookingFlow(existingFlow)
      return existingFlow
    }

    console.log('🆕 Creating new booking flow')

    // Create new flow with optional initial package details
    const hasPackage = !!(initialData.packageDetails?.packagePrice)
    const hasSchedule = !!initialData.date

    const newFlow = {
      photographerId,
      currentStep: hasSchedule ? (hasPackage ? 'addons' : 'schedule') : 'schedule',
      completedSteps: hasSchedule ? ['schedule'] : [],
      scheduleDetails: {
        date: initialData.date || null,
        startTime: initialData.startTime || null,
        endTime: initialData.endTime || null,
        selectedAt: initialData.date ? new Date().toISOString() : null
      },
      packageDetails: initialData.packageDetails ? {
        ...initialData.packageDetails,
        selectedAt: new Date().toISOString()
      } : {
        packageType: null,
        packagePrice: null,
        hoursBooked: null,
        isPhotoVideo: false,
        packageTitle: null,
        selectedAt: null
      },
      addonsDetails: {
        selectedAddons: [],
        totalAddonsPrice: 0,
        selectedAt: null
      },
      locationDetails: {
        city: null,
        state: null,
        locationTitle: null,
        address: null,
        lat: null,
        lng: null,
        selectedAt: null
      },
      travelFeeDetails: {
        travelFee: 0,
        distanceMiles: null,
        nearestMetro: null,
        calculatedAt: null
      },
      accountDetails: {
        isAuthenticated: false,
        userId: null,
        email: null,
        fullName: null,
        phone: null,
        selectedAt: null
      },
      contractDetails: {
        contractSigned: false,
        contractSignatureId: null,
        contractVersion: null,
        signedAt: null,
        selectedAt: null
      },
      validationState: {
        schedule: hasSchedule,
        package: hasPackage,
        addons: false,
        account: false,
        contract: false
      }
    }

    setBookingFlow(newFlow)
    return newFlow
  }, [loadBookingFlow, sessionId])

  // Update schedule details
  const updateScheduleDetails = useCallback((date, startTime = null, endTime = null) => {
    setBookingFlow(prev => {
      // Only require date - times are optional (user selects package, not specific times)
      const scheduleValid = !!date
      const newCompletedSteps = scheduleValid
        ? [...new Set([...prev.completedSteps, 'schedule'])]
        : prev.completedSteps.filter(step => step !== 'schedule')

      // Times are now optional - package selection handles hours/pricing
      // Keep times in state for backwards compatibility but don't require them
      return {
        ...prev,
        scheduleDetails: {
          date,
          startTime,
          endTime,
          selectedAt: new Date().toISOString()
        },
        packageDetails: {
          ...prev.packageDetails
          // Package price and hours are set by package selection, not schedule
        },
        completedSteps: newCompletedSteps,
        currentStep: scheduleValid ? 'packages' : 'schedule', // Changed from 'addons' to 'packages'
        validationState: {
          ...prev.validationState,
          schedule: scheduleValid
        }
      }
    })
  }, [])

  // Update package details
  const updatePackageDetails = useCallback((packageData) => {
    setBookingFlow(prev => {
      const packageValid = !!(packageData?.hoursBooked && packageData?.packagePrice)
      const newCompletedSteps = packageValid
        ? [...new Set([...prev.completedSteps, 'package'])]
        : prev.completedSteps.filter(step => step !== 'package')

      return {
        ...prev,
        packageDetails: {
          ...prev.packageDetails,
          ...packageData,
          selectedAt: new Date().toISOString()
        },
        completedSteps: newCompletedSteps,
        currentStep: packageValid ? 'addons' : 'package',
        validationState: {
          ...prev.validationState,
          package: packageValid
        }
      }
    })
  }, [])

  // Update add-ons details
  const updateAddonsDetails = useCallback((addonsData) => {
    setBookingFlow(prev => {
      const selectedAddons = addonsData?.selectedAddons || []
      const totalAddonsPrice = selectedAddons.reduce((sum, addon) => sum + (addon.price * (addon.qty || 1)), 0)
      const addonsValid = true // Add-ons are optional, so always valid when visited
      const newCompletedSteps = addonsValid
        ? [...new Set([...prev.completedSteps, 'addons'])]
        : prev.completedSteps.filter(step => step !== 'addons')

      return {
        ...prev,
        addonsDetails: {
          selectedAddons,
          totalAddonsPrice,
          selectedAt: new Date().toISOString()
        },
        completedSteps: newCompletedSteps,
        currentStep: addonsValid ? 'account' : 'addons',
        validationState: {
          ...prev.validationState,
          addons: addonsValid
        }
      }
    })
  }, [])

  // Update location details
  const updateLocationDetails = useCallback((locationData) => {
    setBookingFlow(prev => {
      const locationValid = !!(locationData && (locationData.city || locationData.state || locationData.locationTitle))
      const newCompletedSteps = locationValid
        ? [...new Set([...prev.completedSteps, 'location'])]
        : prev.completedSteps.filter(step => step !== 'location')

      return {
        ...prev,
        locationDetails: {
          ...locationData,
          selectedAt: new Date().toISOString()
        },
        completedSteps: newCompletedSteps,
        validationState: {
          ...prev.validationState,
          location: locationValid
        }
      }
    })
  }, [])

  // Update travel fee details
  const updateTravelFeeDetails = useCallback((travelFeeData) => {
    setBookingFlow(prev => ({
      ...prev,
      travelFeeDetails: {
        ...travelFeeData,
        calculatedAt: new Date().toISOString()
      }
    }))
  }, [])

  // Set booking ID (called after booking is created)
  const setBookingId = useCallback((bookingId) => {
    setBookingFlow(prev => ({
      ...prev,
      bookingId
    }))
  }, [])

  // Set videographer ID (called when user adds videographer to booking)
  const setVideographerId = useCallback((videographerId) => {
    setBookingFlow(prev => ({
      ...prev,
      videographerId
    }))
  }, [])

  // Set second photographer ID (called when user adds second photographer to booking)
  const setSecondPhotographerId = useCallback((secondPhotographerId) => {
    setBookingFlow(prev => ({
      ...prev,
      secondPhotographerId
    }))
  }, [])

  // Set engagement booking ID (called when engagement session booking is created)
  const setEngagementBookingId = useCallback((engagementBookingId) => {
    setBookingFlow(prev => ({
      ...prev,
      engagementBookingId
    }))
  }, [])

  // Update account details
  const updateAccountDetails = useCallback((accountData) => {
    setBookingFlow(prev => {
      const accountValid = !!(accountData && accountData.isAuthenticated && accountData.userId)
      const newCompletedSteps = accountValid
        ? [...new Set([...prev.completedSteps, 'account'])]
        : prev.completedSteps.filter(step => step !== 'account')

      return {
        ...prev,
        accountDetails: {
          ...accountData,
          selectedAt: new Date().toISOString()
        },
        completedSteps: newCompletedSteps,
        currentStep: accountValid ? 'contract' : 'account', // Move to contract after account
        validationState: {
          ...prev.validationState,
          account: accountValid
        }
      }
    })
  }, [])

  // Update contract details
  const updateContractDetails = useCallback((contractData) => {
    setBookingFlow(prev => {
      const contractValid = !!(contractData && contractData.contractSigned)
      const newCompletedSteps = contractValid
        ? [...new Set([...prev.completedSteps, 'contract'])]
        : prev.completedSteps.filter(step => step !== 'contract')

      return {
        ...prev,
        contractDetails: {
          ...contractData,
          selectedAt: new Date().toISOString()
        },
        completedSteps: newCompletedSteps,
        currentStep: contractValid ? 'payment' : 'contract',
        validationState: {
          ...prev.validationState,
          contract: contractValid
        }
      }
    })
  }, [])

  // Update payment details
  const updatePaymentDetails = useCallback((paymentData) => {
    setBookingFlow(prev => {
      const paymentValid = !!(paymentData && paymentData.paymentIntentId)
      const newCompletedSteps = paymentValid
        ? [...new Set([...prev.completedSteps, 'payment'])]
        : prev.completedSteps.filter(step => step !== 'payment')

      return {
        ...prev,
        paymentDetails: {
          ...prev.paymentDetails,
          ...paymentData,
          selectedAt: new Date().toISOString()
        },
        completedSteps: newCompletedSteps,
        validationState: {
          ...prev.validationState,
          payment: paymentValid
        }
      }
    })
  }, [])

  // Update payment plan selection
  const updatePaymentPlan = useCallback((paymentPlan) => {
    setBookingFlow(prev => ({
      ...prev,
      paymentDetails: {
        ...prev.paymentDetails,
        paymentPlan,
        selectedAt: new Date().toISOString()
      }
    }))
  }, [])

  // Update promotion details (apply coupon)
  const updatePromotionDetails = useCallback((promotionData) => {
    console.log('📝 updatePromotionDetails called with:', promotionData)
    setBookingFlow(prev => {
      const updated = {
        ...prev,
        promotionDetails: promotionData ? {
          promotionId: promotionData.id,
          code: promotionData.code,
          name: promotionData.name,
          discount_type: promotionData.discount_type,
          discount_value: promotionData.discount_value,
          free_addons: promotionData.free_addons || [],
          appliedAt: new Date().toISOString()
        } : {
          promotionId: null,
          code: null,
          name: null,
          discount_type: null,
          discount_value: null,
          free_addons: [],
          appliedAt: null
        }
      }
      console.log('📝 Context updated - new promotionDetails:', updated.promotionDetails)
      return updated
    })
  }, [])

  // Mark payment as complete
  const markPaymentComplete = useCallback((paymentIntentId, receiptUrl) => {
    setBookingFlow(prev => {
      const newCompletedSteps = [...new Set([...prev.completedSteps, 'payment'])]

      return {
        ...prev,
        paymentDetails: {
          ...prev.paymentDetails,
          paymentCompleted: true,
          paymentIntentId: paymentIntentId || prev.paymentDetails?.paymentIntentId || null,
          receiptUrl: receiptUrl || prev.paymentDetails?.receiptUrl || null,
          paidAt: new Date().toISOString(),
          selectedAt: new Date().toISOString()
        },
        completedSteps: newCompletedSteps,
        validationState: {
          ...prev.validationState,
          payment: true
        }
      }
    })
  }, [])

  // Check if a step can be accessed
  const canAccessStep = useCallback((stepName) => {
    const step = BOOKING_STEPS.find(s => s.id === stepName)
    if (!step) return false

    // First step (schedule) is always accessible
    if (step.order === 0) return true

    // Special requirement for contract step: must have bookingId
    if (stepName === 'contract') {
      // Check all previous steps are completed
      for (let i = 0; i < step.order; i++) {
        const prevStep = BOOKING_STEPS[i]
        if (!bookingFlow.completedSteps.includes(prevStep.id)) {
          return false
        }
      }
      // Additional requirement: must have bookingId from account setup
      return !!bookingFlow.bookingId
    }

    // Special requirement for payment step: contract must be signed
    if (stepName === 'payment') {
      // Check all previous steps are completed
      for (let i = 0; i < step.order; i++) {
        const prevStep = BOOKING_STEPS[i]
        if (!bookingFlow.completedSteps.includes(prevStep.id)) {
          return false
        }
      }
      // Additional requirement: contract must be signed
      return bookingFlow.contractDetails.contractSigned === true
    }

    // Check if all previous steps are completed
    for (let i = 0; i < step.order; i++) {
      const prevStep = BOOKING_STEPS[i]
      if (!bookingFlow.completedSteps.includes(prevStep.id)) {
        return false
      }
    }

    return true
  }, [bookingFlow.completedSteps, bookingFlow.contractDetails.contractSigned, bookingFlow.bookingId])

  // Navigate to a specific step (with validation)
  const goToStep = useCallback((stepName) => {
    if (!canAccessStep(stepName)) {
      console.warn(`Cannot access step "${stepName}" - prerequisites not met`)
      return false
    }

    setBookingFlow(prev => ({
      ...prev,
      currentStep: stepName
    }))
    return true
  }, [canAccessStep])

  // Get current step configuration
  const getCurrentStepConfig = useCallback(() => {
    return BOOKING_STEPS.find(step => step.id === bookingFlow.currentStep)
  }, [bookingFlow.currentStep])

  // Get steps for stepper component
  const getStepsForStepper = useCallback(() => {
    return BOOKING_STEPS.map(step => ({
      id: step.id,
      label: step.label,
      status: bookingFlow.completedSteps.includes(step.id)
        ? 'completed'
        : step.id === bookingFlow.currentStep
          ? 'current'
          : 'upcoming'
    }))
  }, [bookingFlow.completedSteps, bookingFlow.currentStep])

  // Load booking from database (for abandoned cart resume)
  const loadFromBooking = useCallback((booking) => {
    console.log('📥 Loading booking into flow:', booking.id)

    // Extract booking_data (contains package, addons, etc.)
    const bookingData = booking.booking_data || {}

    // Build flow state from database booking
    const loadedFlow = {
      photographerId: booking.photographer_id,
      bookingId: booking.id,
      currentStep: 'contract', // Skip to contract signing (must sign before payment)
      completedSteps: ['package', 'addons', 'account'], // Mark previous steps complete, but not contract

      scheduleDetails: {
        date: bookingData.date || null,
        startTime: bookingData.startTime || null,
        endTime: bookingData.endTime || null,
        selectedAt: booking.created_at
      },

      packageDetails: {
        packageType: booking.package_type,
        packagePrice: bookingData.packagePrice || null,
        hoursBooked: booking.hours_booked,
        isPhotoVideo: bookingData.isPhotoVideo || false,
        packageTitle: bookingData.packageTitle || null,
        selectedAt: booking.created_at
      },

      addonsDetails: {
        selectedAddons: bookingData.addons || [],
        totalAddonsPrice: (bookingData.addons || []).reduce((sum, addon) => sum + (addon.price * (addon.qty || 1)), 0),
        selectedAt: booking.created_at
      },

      locationDetails: {
        city: bookingData.city || null,
        state: bookingData.state || null,
        locationTitle: bookingData.locationTitle || null,
        address: bookingData.address || null,
        selectedAt: booking.created_at
      },

      travelFeeDetails: {
        travelFee: bookingData.travelFee || 0,
        distanceMiles: bookingData.distanceMiles || null,
        nearestMetro: bookingData.nearestMetro || null,
        calculatedAt: booking.created_at
      },

      accountDetails: {
        isAuthenticated: true,
        userId: booking.customer_id,
        email: booking.customer?.email || null,
        fullName: booking.customer?.full_name || null,
        phone: booking.customer?.phone || null,
        selectedAt: booking.created_at
      },

      contractDetails: {
        contractSigned: false, // User still needs to sign
        contractSignatureId: null,
        contractVersion: null,
        signedAt: null,
        selectedAt: null
      },

      paymentDetails: {
        paymentCompleted: false,
        paymentMethod: null,
        paymentPlan: bookingData.paymentPlan || 'full',
        paymentIntentId: null,
        receiptUrl: null,
        paidAt: null,
        selectedAt: null
      },

      validationState: {
        schedule: true,
        package: true,
        addons: true,
        account: true,
        contract: false, // User needs to sign contract
        payment: false
      }
    }

    console.log('✅ Booking flow loaded from database:', {
      bookingId: booking.id,
      packageType: booking.package_type,
      hours: booking.hours_booked,
      total: booking.total_amount
    })

    setBookingFlow(loadedFlow)
    return loadedFlow
  }, [])

  // Reset booking flow
  const resetBookingFlow = useCallback((photographerId = null) => {
    const newFlow = {
      photographerId,
      bookingId: null,
      currentStep: 'schedule',
      completedSteps: [],
      scheduleDetails: {
        date: null,
        startTime: null,
        endTime: null,
        selectedAt: null
      },
      packageDetails: {
        packageType: null,
        packagePrice: null,
        hoursBooked: null,
        isPhotoVideo: false,
        packageTitle: null,
        selectedAt: null
      },
      addonsDetails: {
        selectedAddons: [],
        totalAddonsPrice: 0,
        selectedAt: null
      },
      locationDetails: {
        city: null,
        state: null,
        locationTitle: null,
        address: null,
        lat: null,
        lng: null,
        selectedAt: null
      },
      travelFeeDetails: {
        travelFee: 0,
        distanceMiles: null,
        nearestMetro: null,
        calculatedAt: null
      },
      accountDetails: {
        isAuthenticated: false,
        userId: null,
        email: null,
        fullName: null,
        phone: null,
        selectedAt: null
      },
      contractDetails: {
        contractSigned: false,
        contractSignatureId: null,
        contractVersion: null,
        signedAt: null,
        selectedAt: null
      },
      validationState: {
        schedule: false,
        package: false,
        location: false,
        addons: false,
        account: false,
        contract: false
      }
    }

    setBookingFlow(newFlow)

    // Clear from localStorage if photographerId provided
    if (photographerId) {
      localStorage.removeItem(getStorageKey(photographerId))
    }
  }, [getStorageKey])

  // Get package price
  const getPackagePrice = useCallback(() => {
    return bookingFlow.packageDetails.packagePrice || 0
  }, [bookingFlow.packageDetails.packagePrice])

  // Get total price (package + add-ons + travel fee)
  const getTotalPrice = useCallback(() => {
    const packagePrice = getPackagePrice()
    const addonsPrice  = bookingFlow.addonsDetails.totalAddonsPrice || 0
    const travelFee    = bookingFlow.travelFeeDetails.travelFee || 0
    return packagePrice + addonsPrice + travelFee
  }, [getPackagePrice, bookingFlow.addonsDetails.totalAddonsPrice, bookingFlow.travelFeeDetails.travelFee])

  // Get selected add-ons total
  const getSelectedAddonsTotal = useCallback(() => {
    return bookingFlow.addonsDetails.totalAddonsPrice || 0
  }, [bookingFlow.addonsDetails.totalAddonsPrice])

  // Check if booking flow has any progress
  const hasProgress = useCallback(() => {
    return bookingFlow.completedSteps.length > 0 ||
           bookingFlow.scheduleDetails.date ||
           bookingFlow.packageDetails.packageType
  }, [bookingFlow])

  const value = {
    // State
    bookingFlow,
    sessionId,

    // Actions
    initializeBookingFlow,
    updateScheduleDetails,
    updatePackageDetails,
    updateAddonsDetails,
    updateLocationDetails,
    updateTravelFeeDetails,
    setBookingId,
    setVideographerId,
    setSecondPhotographerId,
    setEngagementBookingId,
    updateAccountDetails,
    updateContractDetails,
    updatePaymentDetails,
    updatePaymentPlan,
    updatePromotionDetails,
    markPaymentComplete,
    goToStep,
    loadFromBooking,
    resetBookingFlow,

    // Utilities
    canAccessStep,
    getCurrentStepConfig,
    getStepsForStepper,
    hasProgress,
    getPackagePrice,
    getTotalPrice,
    getSelectedAddonsTotal,

    // Constants
    BOOKING_STEPS
  }

  return (
    <BookingFlowContext.Provider value={value}>
      {children}
    </BookingFlowContext.Provider>
  )
}

export default BookingFlowContext